/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { CheckSquare, Plus, Trash2, Pencil, Search, ChevronLeft, ChevronRight, Upload, Download } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

const CATEGORIES = ["general", "records", "history"] as const;
type Category = typeof CATEGORIES[number];

const CATEGORY_LABELS: Record<Category, string> = {
  general: "General",
  records: "Records",
  history: "History",
};

const CATEGORY_COLORS: Record<Category, string> = {
  general: "bg-blue-100 text-blue-700",
  records: "bg-amber-100 text-amber-700",
  history: "bg-purple-100 text-purple-700",
};

const CATEGORY_ICONS: Record<Category, string> = {
  general: "⚽", records: "🏆", history: "📖",
};

interface TrueFalseQuestion {
  id: number;
  statement: string;
  isTrue: boolean;
  category: string;
  order: number;
  isActive: boolean;
}

const PAGE_SIZE = 15;

const emptyForm = {
  category: "general" as Category,
  statement: "",
  isTrue: true,
  order: "" as string | number,
};
type FormState = typeof emptyForm;

interface ImportRow {
  statement: string;
  isTrue: boolean;
  category: Category;
  order?: number;
}

function parseImport(raw: string): { rows: ImportRow[]; error: string | null } {
  const text = raw.trim();
  if (!text) return { rows: [], error: "Nothing to import" };
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return { rows: [], error: "Must be a JSON array" };
    const rows: ImportRow[] = [];
    for (let i = 0; i < parsed.length; i++) {
      const item = parsed[i];
      if (typeof item.statement !== "string" || !item.statement.trim())
        return { rows: [], error: `Row ${i + 1}: "statement" is required` };
      if (typeof item.isTrue !== "boolean")
        return { rows: [], error: `Row ${i + 1}: "isTrue" must be true or false` };
      if (!CATEGORIES.includes(item.category))
        return { rows: [], error: `Row ${i + 1}: "category" must be general, records, or history` };
      rows.push({
        statement: item.statement.trim(),
        isTrue: item.isTrue,
        category: item.category as Category,
        order: typeof item.order === "number" ? item.order : undefined,
      });
    }
    return { rows, error: null };
  } catch {
    return { rows: [], error: "Invalid JSON — check your syntax" };
  }
}

const IMPORT_TEMPLATE = JSON.stringify(
  [
    { statement: "Cristiano Ronaldo has won 5 Ballon d'Or awards.", isTrue: true, category: "general" },
    { statement: "The first FIFA World Cup was held in 1934.", isTrue: false, category: "history" },
    { statement: "Lionel Messi has never scored a hat-trick in a World Cup.", isTrue: true, category: "records" },
  ],
  null,
  2
);

export default function TrueFalsePage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TrueFalseQuestion | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<{ statement?: string }>({});
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<Category | "all">("all");
  const [page, setPage] = useState(1);

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<ImportRow[] | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const { data, isLoading } = useQuery<TrueFalseQuestion[]>({
    queryKey: ["quiz", "true-or-false"],
    queryFn: async () => {
      const r = await apiClient.get(API_ENDPOINTS.QUIZ.TF_LIST) as unknown as TrueFalseQuestion[] | null;
      return r ?? [];
    },
  });
  const questions = data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["quiz", "true-or-false"] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: object) => apiClient.post(API_ENDPOINTS.QUIZ.TF_CREATE, d),
    onSuccess: () => { toast.success("Question created"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to create"),
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: object }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.TF_UPDATE(id), d),
    onSuccess: () => { toast.success("Updated"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to update"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.QUIZ.TF_DELETE(id)),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to delete"),
  });

  const { mutate: toggle } = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.TF_UPDATE(id), { isActive }),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: questions.length });
    setOpen(true);
  };

  const openEdit = (q: TrueFalseQuestion) => {
    setEditing(q);
    setForm({
      category: q.category as Category,
      statement: q.statement,
      isTrue: q.isTrue,
      order: q.order,
    });
    setOpen(true);
  };

  const closeDialog = () => { setOpen(false); setEditing(null); setErrors({}); };

  const openImport = () => {
    setImportText("");
    setImportError(null);
    setImportPreview(null);
    setImportOpen(true);
  };

  const handleImportParse = () => {
    const { rows, error } = parseImport(importText);
    if (error) { setImportError(error); setImportPreview(null); return; }
    setImportError(null);
    setImportPreview(rows);
  };

  const handleImportSubmit = async () => {
    if (!importPreview?.length) return;
    setIsImporting(true);
    try {
      const baseOrder = questions.length;
      await Promise.all(
        importPreview.map((row, i) =>
          apiClient.post(API_ENDPOINTS.QUIZ.TF_CREATE, {
            ...row,
            order: row.order ?? baseOrder + i,
          })
        )
      );
      toast.success(`${importPreview.length} questions imported`);
      invalidate();
      setImportOpen(false);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Import failed");
    } finally {
      setIsImporting(false);
    }
  };
  const set = (k: keyof FormState, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.statement.trim()) { setErrors({ statement: "Statement is required" }); return; }
    if (!editing) {
      const dup = questions.find((q) => q.statement.trim().toLowerCase() === form.statement.trim().toLowerCase());
      if (dup) { setErrors({ statement: "This statement already exists" }); return; }
    }
    setErrors({});
    const payload = {
      category: form.category,
      statement: form.statement.trim(),
      isTrue: form.isTrue,
      order: form.order !== "" ? Number(form.order) : questions.length,
    };
    if (editing) update({ id: editing.id, d: payload });
    else create(payload);
  };

  const filtered = [...questions]
    .sort((a, b) => a.order - b.order)
    .filter((q) => {
      if (categoryFilter !== "all" && q.category !== categoryFilter) return false;
      if (!search.trim()) return true;
      return q.statement.toLowerCase().includes(search.toLowerCase());
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeCount = questions.filter((q) => q.isActive).length;
  const trueCount = questions.filter((q) => q.isTrue).length;

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <CheckSquare className="h-8 w-8 text-teal-500" />
            True or False
          </h1>
          <p className="text-gray-500 mt-1">
            {questions.length} questions · {activeCount} active
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openImport} className="border-teal-300 text-teal-700 hover:bg-teal-50">
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import
          </Button>
          <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-5 gap-3 mb-5">
        {/* True / False counts */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-green-700">{trueCount}</p>
          <p className="text-xs text-green-600 mt-0.5 font-medium">TRUE</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-red-700">{questions.length - trueCount}</p>
          <p className="text-xs text-red-600 mt-0.5 font-medium">FALSE</p>
        </div>
        {/* Category filter cards */}
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => { setCategoryFilter(categoryFilter === c ? "all" : c); setPage(1); }}
            className={cn(
              "bg-white rounded-xl border shadow-sm p-3 text-center transition-colors",
              categoryFilter === c && "ring-2 ring-teal-500 border-teal-300",
            )}
          >
            <p className="text-xl">{CATEGORY_ICONS[c]}</p>
            <p className="text-lg font-bold text-gray-900">{questions.filter((q) => q.category === c).length}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{CATEGORY_LABELS[c]}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search statements…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <CheckSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{search ? "No matches" : "No questions yet"}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-medium w-8">#</th>
                  <th className="text-left px-4 py-2.5 font-medium">Statement</th>
                  <th className="text-left px-4 py-2.5 font-medium w-24">Category</th>
                  <th className="text-left px-4 py-2.5 font-medium w-20">Answer</th>
                  <th className="text-left px-4 py-2.5 font-medium w-20">Active</th>
                  <th className="px-4 py-2.5 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((q) => (
                  <tr
                    key={q.id}
                    className={cn("hover:bg-gray-50 transition-colors", !q.isActive && "opacity-50")}
                  >
                    <td className="px-4 py-3 text-gray-400 text-xs">{q.order}</td>
                    <td className="px-4 py-3 text-gray-800 leading-relaxed">{q.statement}</td>
                    <td className="px-4 py-3">
                      <Badge className={cn("text-[10px]", CATEGORY_COLORS[q.category as Category])}>
                        {CATEGORY_ICONS[q.category as Category]} {CATEGORY_LABELS[q.category as Category]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full",
                          q.isTrue
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700",
                        )}
                      >
                        {q.isTrue ? "✓ TRUE" : "✗ FALSE"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={q.isActive}
                        onCheckedChange={(v) => toggle({ id: q.id, isActive: v })}
                        className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-400"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(q)}>
                          <Pencil className="h-3.5 w-3.5 text-gray-400" />
                        </Button>
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => { if (confirm("Delete this question?")) remove(q.id); }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                  .reduce<(number | "…")[]>((acc, n, idx, arr) => {
                    if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === "…" ? (
                      <span key={`e-${i}`} className="px-1">…</span>
                    ) : (
                      <Button
                        key={n}
                        variant={n === safePage ? "default" : "outline"}
                        size="sm"
                        className={cn("h-8 w-8 p-0", n === safePage && "bg-teal-600 hover:bg-teal-700 text-white")}
                        onClick={() => setPage(n as number)}
                      >
                        {n}
                      </Button>
                    )
                  )}
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Question" : "New True or False Question"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{CATEGORY_ICONS[c]} {CATEGORY_LABELS[c]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Display Order</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="0"
                  min={0}
                />
              </div>
            </div>

            <div>
              <Label className="mb-1 block text-sm">Statement <span className="text-red-500">*</span></Label>
              <Textarea
                value={form.statement}
                onChange={(e) => { set("statement", e.target.value); if (e.target.value.trim()) setErrors((er) => ({ ...er, statement: undefined })); }}
                placeholder="e.g. Cristiano Ronaldo is the all-time top scorer in UEFA Champions League history."
                rows={3}
                className={cn("resize-none", errors.statement && "border-red-400")}
              />
              {errors.statement && <p className="text-xs text-red-500 mt-1 font-medium">{errors.statement}</p>}
            </div>

            {/* TRUE / FALSE toggle — prominent visual selector */}
            <div>
              <Label className="mb-2 block text-sm">Correct Answer</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => set("isTrue", true)}
                  className={cn(
                    "flex flex-col items-center gap-1.5 rounded-xl border-2 py-4 px-3 font-bold text-sm transition-all",
                    form.isTrue
                      ? "border-green-500 bg-green-50 text-green-700 shadow-sm"
                      : "border-gray-200 bg-white text-gray-400 hover:border-gray-300",
                  )}
                >
                  <span className="text-2xl">✓</span>
                  TRUE
                </button>
                <button
                  type="button"
                  onClick={() => set("isTrue", false)}
                  className={cn(
                    "flex flex-col items-center gap-1.5 rounded-xl border-2 py-4 px-3 font-bold text-sm transition-all",
                    !form.isTrue
                      ? "border-red-500 bg-red-50 text-red-700 shadow-sm"
                      : "border-gray-200 bg-white text-gray-400 hover:border-gray-300",
                  )}
                >
                  <span className="text-2xl">✗</span>
                  FALSE
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isCreating || isUpdating ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-teal-600" />
              Bulk Import True or False Questions
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Paste a JSON array of questions. Each item needs <code className="bg-gray-100 px-1 rounded text-xs">statement</code>, <code className="bg-gray-100 px-1 rounded text-xs">isTrue</code>, and <code className="bg-gray-100 px-1 rounded text-xs">category</code>.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-teal-600 text-xs gap-1"
                onClick={() => {
                  setImportText(IMPORT_TEMPLATE);
                  setImportError(null);
                  setImportPreview(null);
                }}
              >
                <Download className="h-3 w-3" />
                Load template
              </Button>
            </div>

            <Textarea
              value={importText}
              onChange={(e) => { setImportText(e.target.value); setImportError(null); setImportPreview(null); }}
              placeholder={`[\n  { "statement": "...", "isTrue": true, "category": "general" },\n  ...\n]`}
              className="font-mono text-xs resize-none overflow-y-scroll"
              style={{ height: "240px" }}
            />

            {importError && (
              <p className="text-sm text-red-600 font-medium bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {importError}
              </p>
            )}

            {importPreview && (
              <div className="border border-teal-200 rounded-lg overflow-hidden">
                <div className="bg-teal-50 px-3 py-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-teal-700">
                    Preview — {importPreview.length} question{importPreview.length !== 1 ? "s" : ""} ready to import
                  </span>
                </div>
                <div className="max-h-52 overflow-y-auto divide-y divide-gray-100">
                  {importPreview.map((row, i) => (
                    <div key={i} className="flex items-start gap-3 px-3 py-2 text-sm">
                      <span className={cn(
                        "shrink-0 text-xs font-bold px-1.5 py-0.5 rounded-full mt-0.5",
                        row.isTrue ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      )}>
                        {row.isTrue ? "T" : "F"}
                      </span>
                      <span className="flex-1 text-gray-800 leading-snug">{row.statement}</span>
                      <Badge className={cn("shrink-0 text-[10px]", CATEGORY_COLORS[row.category])}>
                        {CATEGORY_ICONS[row.category]} {CATEGORY_LABELS[row.category]}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            {!importPreview ? (
              <Button
                onClick={handleImportParse}
                disabled={!importText.trim()}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                Parse &amp; Preview
              </Button>
            ) : (
              <Button
                onClick={handleImportSubmit}
                disabled={isImporting}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                {isImporting ? "Importing…" : `Import ${importPreview.length} Questions`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
